// API de Órdenes (solo búsqueda por código, según openapi.yaml)
import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import {
  buildOrderPartCommandIdempotencyKey,
  generateIdempotencyKey,
  type OrderPartCommandAction,
} from '@/services/utils/idempotency';
import { setAppNotice } from '@/app/store/appShellSlice';

// URL base de la API (ajústala via env var)
const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

// Tipos según el esquema OrderByCode del OpenAPI.
export interface OrderByCode {
  id: number;
  company_id: number;
  order_code: string;
  imei_serie: string;
  diagnostic: string;
  reported_failure: string;
  observations?: string;
  order_status_id: number;
  order_status_name: string;
  has_lock: boolean;
  lock_type?: number | null;
  lock_info?: string | null;
  warranty_id: number;
  warranty_days: number;
  repair_cost: number;
  total_cost: number;
  paid_amount: number;
  client_id: number;
  client_name: string;
  equipment_type_id: number;
  equipment_type: string;
  equipment_model_id: number;
  equipment_model: string;
  equipment_brand_id: number;
  equipment_brand: string;
  created_at: string;
  updated_at: string;
  has_pending_serial_parts: boolean; // Compatibilidad: ahora refleja si existen piezas bloqueantes
  pending_serial_count: number; // Compatibilidad: ahora refleja el conteo de piezas bloqueantes
  has_blocking_order_parts?: boolean;
  blocking_order_parts_count?: number;
  requested_order_parts_count?: number;
}

/**
 * Se define la solicitud para obtener resumenes de ordenes por lote.
 */
export type OrderSummaryBatchRequest = {
  ids: number[];
};

/**
 * Se define el resumen de orden usado en los agregados de pagos.
 */
export type OrderSummaryBatchItem = {
  id: number;
  order_code: string;
  order_status_id: number;
  order_status_name: string;
  total_cost: number;
  paid_amount: number;
  client_id: number;
  client_name: string;
  equipment_type: string;
  equipment_brand: string;
  equipment_model: string;
  created_at: string;
  updated_at: string;
};

/**
 * Se define la respuesta del lote de resumenes de ordenes.
 */
export type OrderSummaryBatchData = {
  items: OrderSummaryBatchItem[];
  missing_ids: number[];
};

// Respuesta estándar del backend (ApiResponseBase) con data tipada
export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

// Listado paginado de órdenes (GET /v1/orders)
export interface ServiceOrderItem {
  id: number;
  order_code?: string;
  intake_id?: number;
  intake_code?: string;
  imei_serie: string;
  diagnostic: string;
  reported_failure: string;
  observations?: string;
  order_status_id: number;
  order_status_name: string;
  has_lock: boolean;
  lock_type?: number | null;
  lock_info?: string | null;
  warranty_id: number;
  repair_cost: number;
  total_cost: number;
  paid_amount: number;
  client_id: number;
  client_name: string;
  assigned_to_user_id?: number | null;
  updated_by_user_id?: number | null;
  client_contact_number: string;
  equipment_type_id: number;
  equipment_type: string;
  equipment_model_id: number;
  equipment_model: string;
  equipment_brand_id: number;
  equipment_brand: string;
  created_at: string;
  updated_at: string;
  has_pending_serial_parts: boolean; // Compatibilidad: ahora refleja si existen piezas bloqueantes
  pending_serial_count: number; // Compatibilidad: ahora refleja el conteo de piezas bloqueantes
  has_blocking_order_parts?: boolean;
  blocking_order_parts_count?: number;
  requested_order_parts_count?: number;
}

export interface PagedServiceOrders {
  total: number;
  page: number;
  page_size: number;
  items: ServiceOrderItem[];
}

// Ticket PDF payload (OpenAPI: TicketPdfData)
export interface TicketPdfData {
  pdf_base64: string;
}

// Respuesta del endpoint de emisión de ticket POS.
export type PosSaleTicketIssueData = {
  ticket_id: number;
  sale_id: number;
  pdf_url?: string;
  pdf_base64?: string;
  copy_no: number;
  idempotent: boolean;
  issued_at?: string;
};

// Respuesta del endpoint de reimpresión de ticket POS.
export type PosSaleTicketReprintData = {
  ticket_id: number;
  sale_id: number;
  pdf_url?: string;
  copy_no: number;
  idempotent: boolean;
  printed_at?: string;
};

// Respuesta del endpoint de emisión de ticket de corte diario POS.
export type PosDailyClosureTicketIssueData = {
  ticket_id: number;
  closure_id: number;
  pdf_url?: string;
  pdf_base64?: string;
  copy_no: number;
  idempotent: boolean;
  issued_at?: string;
};

export interface OrderPhoto {
  id: number;
  url: string;
  name?: string;
  purpose?: string;
  uploaded_at?: string;
}

export interface OrderSignature {
  id: number;
  url: string;
  mime_type: string;
  signer_name: string;
  signer_role?: string | null;
  terms_version: string;
  created_at: string;
}

export type OrderRegister = {
  imei_serie: string;
  diagnostic: string;
  reported_failure: string;
  // order_status_id?: number; // omitido segun instruccion
  has_lock?: boolean;
  lock_type?: number | null;
  lock_info?: string | null;
  warranty_id: number;
  repair_cost: number;
  total_cost: number;
  paid_amount: number;
  client_id: number;
  equipment_type_id: number;
  equipment_model_id: number;
  equipment_brand_id: number;
};

// Payload para sincronizar piezas aprobadas de una orden
export type OrderPartPayload = {
  sku: string;
  quantity: number;
  unit_price: number;
  name?: string;
};

export type OrderPartLineStatus = 'requested' | 'reserved' | 'consumed' | 'released';

export type OrderPartDTO = OrderPartResponse & {
  serial_code?: string | null;
  track_serial?: boolean;
  status?: string | null;
};

export type OrderPartResponse = {
  id: number;
  sku: string;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
  line_status: OrderPartLineStatus;
  qty_requested: number;
  qty_reserved: number;
  qty_consumed: number;
  qty_released: number;
};

export type UnavailableOrderPart = {
  order_part_id: number;
  service_order_id: number;
  order_code: string;
  branch_id: number;
  part_inventory_id: number;
  sku: string;
  name: string;
  line_status: OrderPartLineStatus;
  qty_requested: number;
  qty_reserved: number;
  qty_consumed: number;
  qty_released: number;
  qty_pending: number;
  qty_on_hand: number;
  qty_reserved_stock: number;
  qty_available: number;
  qty_missing: number;
  supplier_id?: number | null;
  supplier_name?: string | null;
};

export type PagedUnavailableOrderParts = {
  total: number;
  page: number;
  page_size: number;
  items: UnavailableOrderPart[];
};

export type StartRepairIssue = {
  part_id: number;
  sku: string;
  name: string;
  line_status: OrderPartLineStatus;
  message: string;
};

export type StartRepairResult = {
  order_id: number;
  order_status_id: number;
  status: 'applied' | 'unchanged' | 'blocked';
  requested_parts_count: number;
  reserved_parts_count: number;
  failed_parts_count: number;
  failed_items?: StartRepairIssue[];
};

export type OrderEvidencePhotoRequest = {
  content: string;
  content_hash?: string;
  purpose?: string;
};

export type OrderEvidenceSignatureRequest = {
  content: string;
  content_hash?: string;
  mime?: string;
  signer_name: string;
  signer_role?: string | null;
  terms_version: string;
  terms_text: string;
};

export type OrderEvidenceRequest = {
  photos?: OrderEvidencePhotoRequest[];
  signature?: OrderEvidenceSignatureRequest;
};

export type OrderEvidenceResponse = {
  order_id: number;
  photos_replay: boolean;
  signature_replay: boolean;
  replay: boolean;
  photos?: OrderPhoto[];
  signature?: OrderSignature | null;
};

type OrderPartLineCommandArgs = {
  id: number | string;
  partId: number | string;
  idempotencyKey?: string;
};

type GetUnavailableOrderPartsArgs = {
  branch_id?: number;
  supplier_id?: number;
  service_order_id?: number;
  page?: number;
  page_size?: number;
};

const buildOrderPartCommandHeaders = (
  action: OrderPartCommandAction,
  orderId: number | string,
  partId: number | string,
  idempotencyKey?: string,
) => ({
  'X-Idempotency-Key':
    idempotencyKey ?? buildOrderPartCommandIdempotencyKey(action, orderId, partId),
});

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['Orders', 'WarrantyApplied', 'OrderSearch'],
  endpoints: (builder) => ({
    // POST /v1/orders/summary/batch
    postOrdersSummaryBatch: builder.mutation<OrderSummaryBatchData, OrderSummaryBatchRequest>({
      query: (body) => ({ url: '/v1/orders/summary/batch', method: 'POST', body }),
      transformResponse: (
        response: ApiResponseBase<OrderSummaryBatchData> | OrderSummaryBatchData,
      ) => {
        if (response && typeof response === 'object' && 'data' in response) {
          return response.data ?? { items: [], missing_ids: [] };
        }
        return response;
      },
      transformErrorResponse: handlerErrorResponse('Orders.SummaryBatch'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener resumen de órdenes',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.SummaryBatch'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.summary.batch',
              endpointName: e?.endpointName || 'Orders.SummaryBatch',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/orders/warranty-applied
    getWarrantyAppliedList: builder.query<
      ApiResponseBase<PagedServiceOrders>,
      { query?: string; page?: number; page_size?: number; from?: string; to?: string }
    >({
      query: ({ query = '', page = 1, page_size = 20, from, to } = {}) => ({
        url:
          `/v1/orders/warranty-applied` +
          `?query=${encodeURIComponent(query)}` +
          `&page=${encodeURIComponent(String(page))}` +
          `&page_size=${encodeURIComponent(String(page_size))}` +
          (from ? `&from=${encodeURIComponent(from)}` : '') +
          (to ? `&to=${encodeURIComponent(to)}` : ''),
        method: 'GET',
      }),
      transformErrorResponse: handlerErrorResponse('Orders.WarrantyAppliedList'),
      providesTags: ['WarrantyApplied'],
      keepUnusedDataFor: 0,
    }),

    // POST /v1/orders/{id}/refunds
    registerOrderRefund: builder.mutation<
      ApiResponseBase<{ id: number }>,
      {
        id: number | string;
        body: {
          amount: number;
          payment_date_iso: string;
          refund_method_id?: number;
          refund_method_code?: string;
          notes?: string | null;
          reference?: string | null;
        };
        idempotencyKey: string;
      }
    >({
      query: ({ id, body, idempotencyKey }) => ({
        url: `/v1/orders/${id}/refunds`,
        method: 'POST',
        body,
        headers: { 'X-Idempotency-Key': idempotencyKey },
      }),
      transformErrorResponse: handlerErrorResponse('Orders.Refunds'),
      invalidatesTags: ['Orders', 'WarrantyApplied', 'OrderSearch'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar el reembolso',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Refunds'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.refunds',
              endpointName: e?.endpointName || 'Orders.Refunds',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/orders/{id}/budget
    updateOrderBudget: builder.mutation<
      ApiResponseBase<{ id: number; repair_cost?: number; total_cost?: number }>,
      { id: number | string; body: { repair_cost: number; total_cost?: number; reason?: string } }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/budget`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Orders.BudgetUpdate'),
      invalidatesTags: ['Orders', 'WarrantyApplied', 'OrderSearch'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar el presupuesto de la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.BudgetUpdate'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.budget.update',
              endpointName: e?.endpointName || 'Orders.BudgetUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/orders/{id}/parts
    updateOrderParts: builder.mutation<
      ApiResponseBase<{ items: OrderPartResponse[] }>,
      { id: number | string; body: { parts: OrderPartPayload[]; reason?: string } }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/parts`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Orders.PartsUpdate'),
      invalidatesTags: ['Orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al sincronizar las piezas de la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.PartsUpdate'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.parts.update',
              endpointName: e?.endpointName || 'Orders.PartsUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/orders/{id}/parts
    getOrderParts: builder.query<ApiResponseBase<{ items: OrderPartDTO[] }>, number | string>({
      query: (id) => ({ url: `/v1/orders/${id}/parts`, method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Orders.PartsList'),
      providesTags: ['Orders'],
    }),

    // GET /v1/orders/parts/unavailable
    getUnavailableOrderParts: builder.query<
      ApiResponseBase<PagedUnavailableOrderParts>,
      GetUnavailableOrderPartsArgs | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.branch_id != null) params.set('branch_id', String(args.branch_id));
        if (args?.supplier_id != null) params.set('supplier_id', String(args.supplier_id));
        if (args?.service_order_id != null)
          params.set('service_order_id', String(args.service_order_id));
        if (args?.page != null) params.set('page', String(args.page));
        if (args?.page_size != null) params.set('page_size', String(args.page_size));
        const query = params.toString();
        return {
          url: `/v1/orders/parts/unavailable${query ? `?${query}` : ''}`,
          method: 'GET',
        };
      },
      transformErrorResponse: handlerErrorResponse('Orders.UnavailablePartsList'),
      providesTags: ['Orders'],
    }),

    // GET /v1/orders/{id}/parts/unavailable
    getUnavailableOrderPartsByOrder: builder.query<
      ApiResponseBase<{ items: UnavailableOrderPart[] }>,
      number | string
    >({
      query: (id) => ({ url: `/v1/orders/${id}/parts/unavailable`, method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Orders.UnavailablePartsByOrder'),
      providesTags: ['Orders'],
    }),

    startRepairOrder: builder.mutation<
      ApiResponseBase<StartRepairResult>,
      { id: number | string; idempotencyKey?: string }
    >({
      query: ({ id, idempotencyKey }) => ({
        url: `/v1/orders/${id}/start-repair`,
        method: 'POST',
        headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : undefined,
      }),
      transformErrorResponse: handlerErrorResponse('Orders.StartRepair'),
      invalidatesTags: ['Orders', 'WarrantyApplied', 'OrderSearch'],
    }),

    reserveOrderPart: builder.mutation<
      ApiResponseBase<{ item: OrderPartDTO }>,
      OrderPartLineCommandArgs
    >({
      query: ({ id, partId, idempotencyKey }) => ({
        url: `/v1/orders/${id}/parts/${partId}/reserve`,
        method: 'POST',
        headers: buildOrderPartCommandHeaders('reserve', id, partId, idempotencyKey),
      }),
      transformErrorResponse: handlerErrorResponse('Orders.PartReserve'),
      invalidatesTags: ['Orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al reservar la pieza',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.PartReserve'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.parts.reserve',
              endpointName: e?.endpointName || 'Orders.PartReserve',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    consumeOrderPart: builder.mutation<
      ApiResponseBase<{ item: OrderPartDTO }>,
      OrderPartLineCommandArgs
    >({
      query: ({ id, partId, idempotencyKey }) => ({
        url: `/v1/orders/${id}/parts/${partId}/consume`,
        method: 'POST',
        headers: buildOrderPartCommandHeaders('consume', id, partId, idempotencyKey),
      }),
      transformErrorResponse: handlerErrorResponse('Orders.PartConsume'),
      invalidatesTags: ['Orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al consumir la pieza',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.PartConsume'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.parts.consume',
              endpointName: e?.endpointName || 'Orders.PartConsume',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    releaseOrderPart: builder.mutation<
      ApiResponseBase<{ item: OrderPartDTO }>,
      OrderPartLineCommandArgs
    >({
      query: ({ id, partId, idempotencyKey }) => ({
        url: `/v1/orders/${id}/parts/${partId}/release`,
        method: 'POST',
        headers: buildOrderPartCommandHeaders('release', id, partId, idempotencyKey),
      }),
      transformErrorResponse: handlerErrorResponse('Orders.PartRelease'),
      invalidatesTags: ['Orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al liberar la pieza',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.PartRelease'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.parts.release',
              endpointName: e?.endpointName || 'Orders.PartRelease',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/orders?query=&status_id=0&page=1&page_size=20
    getOrdersList: builder.query<
      ApiResponseBase<PagedServiceOrders>,
      { query: string; status_id?: number; page?: number; page_size?: number; group?: 'intake' }
    >({
      query: ({ query, status_id = 0, page = 1, page_size = 20, group }) => ({
        url:
          `/v1/orders` +
          `?query=${encodeURIComponent(query)}` +
          `&status_id=${encodeURIComponent(String(status_id))}` +
          `&page=${encodeURIComponent(String(page))}` +
          `&page_size=${encodeURIComponent(String(page_size))}` +
          (group ? `&group=${encodeURIComponent(group)}` : ''),
        method: 'GET',
      }),
      transformErrorResponse: handlerErrorResponse('Orders.List'),
      providesTags: ['Orders'],
    }),
    // GET /v1/orders/search/{code}
    getOrderByCode: builder.query<ApiResponseBase<OrderByCode>, string>({
      query: (code) => ({
        url: `/v1/orders/search/${encodeURIComponent(code)}`,
        method: 'GET',
      }),
      // Transforma el error a nuestro formato interno
      transformErrorResponse: handlerErrorResponse('Orders.SearchByCode'),
      providesTags: ['OrderSearch'],
    }),

    // GET /v1/orders/{id}
    getOrderById: builder.query<ApiResponseBase<ServiceOrderItem>, number | string>({
      query: (id) => ({
        url: `/v1/orders/${id}`,
        method: 'GET',
      }),
      transformErrorResponse: handlerErrorResponse('Orders.GetById'),
      providesTags: ['Orders', 'OrderSearch'],
    }),

    // PUT /v1/orders/{id}/status
    updateOrderStatus: builder.mutation<
      ApiResponseBase<
        | { id: number; order_status_id: number }
        | {
            order_id: number;
            current_order_status_id: number;
            target_order_status_id: number;
            validation_status: 'blocked' | 'requires_payment' | 'ready';
            code?: string | null;
            message: string;
            parts: {
              has_blocking_order_parts: boolean;
              blocking_order_parts_count: number;
              requested_order_parts_count: number;
            };
            payment: {
              total_cost: number;
              paid_amount: number;
              pending_amount: number;
              requires_payment: boolean;
            };
          }
      >,
      { id: number | string; body: { order_status_id: number; validate_only?: boolean } }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/status`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Orders.StatusUpdate'),
      invalidatesTags: (_result, _error, arg) =>
        arg.body.validate_only ? [] : ['Orders', 'WarrantyApplied', 'OrderSearch'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar el estado de la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.StatusUpdate'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.status.update',
              endpointName: e?.endpointName || 'Orders.StatusUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/orders/{id}/equipment
    updateOrderEquipment: builder.mutation<
      ApiResponseBase<{ id: number }>,
      {
        id: number | string;
        body: {
          imei_serie?: string;
          equipment_type_id: number;
          equipment_model_id: number;
          equipment_brand_id: number;
          has_lock?: boolean;
          lock_type?: number | null;
          lock_info?: string | null;
        };
      }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/equipment`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Orders.EquipmentUpdate'),
      invalidatesTags: ['Orders', 'WarrantyApplied'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar los datos del equipo',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.EquipmentUpdate'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.equipment.update',
              endpointName: e?.endpointName || 'Orders.EquipmentUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/orders/register
    registerServiceOrder: builder.mutation<ApiResponseBase<{ id: number }>, OrderRegister>({
      query: (body) => ({ url: '/v1/orders', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Register'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Register'}${e?.code ? ` — Codigo: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'orders.register',
              endpointName: e?.endpointName || 'Orders.Register',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/orders/{id}/signature
    registerOrderSignature: builder.mutation<
      ApiResponseBase<{
        id: number;
      }>,
      {
        id: number | string;
        body: {
          content: string; // base64 puro o dataURL
          mime?: string;
          signer_name: string;
          signer_role?: string | null;
          terms_version: string;
          terms_text: string;
        };
      }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/signature`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Signature'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar la firma',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Signature'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.signature',
              endpointName: e?.endpointName || 'Orders.Signature',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/orders/{id}/photos
    registerOrderPhotos: builder.mutation<
      ApiResponseBase<{ uploaded: number }>,
      { id: number | string; body: { photos: { content: string; purpose?: string }[] } }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/photos`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Photos'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al subir fotos de la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Photos'}${e?.code ? ` — Codigo: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'orders.photos',
              endpointName: e?.endpointName || 'Orders.Photos',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    registerOrderEvidence: builder.mutation<
      ApiResponseBase<OrderEvidenceResponse>,
      { id: number | string; body: OrderEvidenceRequest; idempotencyKey: string }
    >({
      query: ({ id, body, idempotencyKey }) => ({
        url: `/v1/orders/${id}/evidence`,
        method: 'POST',
        body,
        headers: {
          'X-Idempotency-Key': idempotencyKey,
        },
      }),
      transformErrorResponse: handlerErrorResponse('Orders.Evidence'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar evidencia de la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Evidence'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.evidence',
              endpointName: e?.endpointName || 'Orders.Evidence',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/orders/{id}/deliver
    registerOrderDelivery: builder.mutation<
      ApiResponseBase<{ id: number; uploaded: number }>,
      {
        id: number | string;
        body: {
          recipient_type: 'owner' | 'third_party';
          notes?: string;
          photos?: { content: string; purpose?: string }[];
        };
      }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/deliver`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.Deliver'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar la entrega',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Deliver'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.deliver',
              endpointName: e?.endpointName || 'Orders.Deliver',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/orders/{id}/payments
    registerOrderPayment: builder.mutation<
      ApiResponseBase<{ id: number }>,
      {
        id: number | string;
        body: {
          amount: number;
          payment_date_iso: string;
          payment_method_id?: number; // soporta id o code
          payment_method_code?: string;
          is_deposit?: boolean;
          notes?: string | null;
          reference?: string | null;
        };
        idempotencyKey: string;
      }
    >({
      query: ({ id, body, idempotencyKey }) => ({
        url: `/v1/orders/${id}/payments`,
        method: 'POST',
        body,
        headers: { 'X-Idempotency-Key': idempotencyKey },
      }),
      transformErrorResponse: handlerErrorResponse('Orders.Payments'),
      invalidatesTags: ['Orders', 'WarrantyApplied', 'OrderSearch'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar el pago',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Payments'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.payments',
              endpointName: e?.endpointName || 'Orders.Payments',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/orders/{id}/ticket/print
    printOrderTicket: builder.mutation<
      ApiResponseBase<TicketPdfData>,
      { id: number | string; body?: { signature?: string } }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/ticket/print`, method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Orders.TicketPrint'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al generar el ticket de la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.TicketPrint'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.ticket.print',
              endpointName: e?.endpointName || 'Orders.TicketPrint',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/pos-sales/ticket/issue
    issuePosSaleTicket: builder.mutation<
      PosSaleTicketIssueData,
      {
        body: { sale_id: number; sale_snapshot: unknown };
        idempotencyKey?: string;
      }
    >({
      query: ({ body, idempotencyKey }) => ({
        url: '/v1/pos-sales/ticket/issue',
        method: 'POST',
        body,
        headers: {
          'X-Idempotency-Key': (idempotencyKey || generateIdempotencyKey('pos-ticket-issue')).slice(
            0,
            100,
          ),
        },
      }),
      transformResponse: (resp: ApiResponseBase<PosSaleTicketIssueData>) => resp.data,
      transformErrorResponse: handlerErrorResponse('Orders.PosSaleTicketIssue'),
    }),

    // POST /v1/pos-sales/{sale_id}/ticket/reprint
    reprintPosSaleTicket: builder.mutation<
      PosSaleTicketReprintData,
      {
        saleId: number;
        body?: { reason?: string };
        idempotencyKey?: string;
      }
    >({
      query: ({ saleId, body, idempotencyKey }) => ({
        url: `/v1/pos-sales/${saleId}/ticket/reprint`,
        method: 'POST',
        body,
        headers: {
          // Se genera una key por request para permitir múltiples reimpresiones explícitas.
          'X-Idempotency-Key': (
            idempotencyKey || generateIdempotencyKey(`pos-ticket-reprint-${saleId}`)
          ).slice(0, 100),
        },
      }),
      transformResponse: (resp: ApiResponseBase<PosSaleTicketReprintData>) => resp.data,
      transformErrorResponse: handlerErrorResponse('Orders.PosSaleTicketReprint'),
    }),

    // POST /v1/pos-daily-closures/ticket/issue
    issuePosDailyClosureTicket: builder.mutation<
      PosDailyClosureTicketIssueData,
      {
        body: { closure_id: number; closure_snapshot: unknown };
        idempotencyKey?: string;
      }
    >({
      query: ({ body, idempotencyKey }) => ({
        url: '/v1/pos-daily-closures/ticket/issue',
        method: 'POST',
        body,
        headers: {
          'X-Idempotency-Key': (
            idempotencyKey ||
            generateIdempotencyKey(`pos-daily-closure-ticket-issue-${body.closure_id}`)
          ).slice(0, 100),
        },
      }),
      transformResponse: (resp: ApiResponseBase<PosDailyClosureTicketIssueData>) => resp.data,
      transformErrorResponse: handlerErrorResponse('Orders.PosDailyClosureTicketIssue'),
    }),

    // PUT /v1/orders/{id}/assignee
    updateOrderAssignee: builder.mutation<
      ApiResponseBase<{ id: number; assigned_to_user_id: number | null; updated: number }>,
      {
        id: number | string;
        body: { user_id: number | null; propagate?: 'none' | 'intake_all' | 'intake_unassigned' };
      }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/assignee`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Orders.AssigneeUpdate'),
      invalidatesTags: ['Orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al asignar técnico',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.AssigneeUpdate'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.assignee.update',
              endpointName: e?.endpointName || 'Orders.AssigneeUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/orders/{id}/evaluation
    updateOrderEvaluation: builder.mutation<
      ApiResponseBase<{ id: number }>,
      {
        id: number | string;
        body: {
          reported_failure: string;
          diagnostic: string;
          observations?: string;
          warranty_id?: number;
        };
      }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/evaluation`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Orders.EvaluationUpdate'),
      invalidatesTags: ['Orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar la evaluación',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.EvaluationUpdate'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.evaluation.update',
              endpointName: e?.endpointName || 'Orders.EvaluationUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/orders/{id}/client
    updateOrderClient: builder.mutation<
      ApiResponseBase<{ id: number }>,
      { id: number | string; body: { client_id: number } }
    >({
      query: ({ id, body }) => ({ url: `/v1/orders/${id}/client`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Orders.ClientUpdate'),
      invalidatesTags: ['Orders'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar el cliente',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.ClientUpdate'}${
                e?.code ? ` — Codigo: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.client.update',
              endpointName: e?.endpointName || 'Orders.ClientUpdate',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/orders/{id}/photos
    getOrderPhotos: builder.query<ApiResponseBase<{ items: OrderPhoto[] }>, number | string>({
      query: (id) => ({ url: `/v1/orders/${id}/photos`, method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Orders.PhotosList'),
      providesTags: ['Orders'],
    }),

    // GET /v1/orders/{id}/signature
    getOrderSignature: builder.query<
      ApiResponseBase<{ signature: OrderSignature | null }>,
      number | string
    >({
      query: (id) => ({ url: `/v1/orders/${id}/signature`, method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Orders.SignatureGet'),
      providesTags: ['Orders'],
    }),
  }),
});

export const {
  usePostOrdersSummaryBatchMutation,
  useGetOrderByIdQuery,
  useGetOrderByCodeQuery,
  useLazyGetOrderByCodeQuery,
  useGetOrdersListQuery,
  useLazyGetOrdersListQuery,
  useGetWarrantyAppliedListQuery,
  useRegisterServiceOrderMutation,
  useRegisterOrderSignatureMutation,
  useRegisterOrderPhotosMutation,
  useRegisterOrderDeliveryMutation,
  useRegisterOrderPaymentMutation,
  useRegisterOrderRefundMutation,
  useUpdateOrderBudgetMutation,
  useUpdateOrderPartsMutation,
  useGetOrderPartsQuery,
  useLazyGetOrderPartsQuery,
  useGetUnavailableOrderPartsQuery,
  useLazyGetUnavailableOrderPartsQuery,
  useGetUnavailableOrderPartsByOrderQuery,
  useLazyGetUnavailableOrderPartsByOrderQuery,
  useStartRepairOrderMutation,
  useReserveOrderPartMutation,
  useConsumeOrderPartMutation,
  useReleaseOrderPartMutation,
  usePrintOrderTicketMutation,
  useIssuePosSaleTicketMutation,
  useReprintPosSaleTicketMutation,
  useIssuePosDailyClosureTicketMutation,
  useUpdateOrderStatusMutation,
  useUpdateOrderEquipmentMutation,
  useUpdateOrderEvaluationMutation,
  useUpdateOrderClientMutation,
  useUpdateOrderAssigneeMutation,
  useGetOrderPhotosQuery,
  useGetOrderSignatureQuery,
} = ordersApi;
